import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';

import { ENABLE_ADVISOR_GROUP_GRAPH_COLORS } from '@/constants/featureFlags';
import { defaultChartColors } from '@/constants/defaultColors';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGetAdvisorGroupColorsQuery } from '@/store/colorsApi';
import type { WARootState } from '@/store/store';
import { deriveAdvisorGroupId } from '@/utils/deriveAdvisorGroupId';

type UseGraphColorsOptions = {
  enabled?: boolean;
};

export function useGraphColors(options?: UseGraphColorsOptions): string[] {
  const enabled = options?.enabled ?? ENABLE_ADVISOR_GROUP_GRAPH_COLORS;

  const authLoaded = useAuthIsLoaded();
  const userInfo = useSelector((state: WARootState) => state.appView.UserInfo);
  const advisorGroupId = useMemo(
    () => deriveAdvisorGroupId(userInfo),
    [userInfo],
  );

  const { data: advisorGroupColors } = useGetAdvisorGroupColorsQuery(
    enabled && authLoaded && typeof advisorGroupId === 'number'
      ? { advisorGroupId }
      : skipToken,
  );

  return useMemo(() => {
    if (!enabled) {
      return defaultChartColors;
    }

    const custom = advisorGroupColors ?? [];
    if (custom.length === 0) {
      return defaultChartColors;
    }

    if (custom.length >= defaultChartColors.length) {
      return custom;
    }

    return [...custom, ...defaultChartColors.slice(custom.length)];
  }, [advisorGroupColors, enabled]);
}
